/* exported baseLogger */
var _ = require('lodash'),
  baseLogger,
  cache = require('../models/cache'),
  CONST_LOGGING = require('../constants/logging'),
  hasInitialized = false,
  jsn = require('jsnlog').JL,
  LoggerCollection = {},
  SetupLogger = require('./setupLogger');

var privateMethods = {
  /**
   * A helper utility to flush the mock loggers out to the real logger instance. This should only be triggered if the logging framework has been initialized
   */
  outputQueuedLogs: function () {
    // Flush any SetupLoggers that were created and replace them with JSN loggers
    _.each(LoggerCollection, function (Logger, namespace) {
      if (Logger.getLogQueue !== undefined) {
        LoggerCollection[namespace] = jsn(namespace);

        _.each(Logger.getLogQueue(), function (log) {
          LoggerCollection[namespace][log.type](log.message);
          Logger.LogQueue = _.without(Logger.LogQueue, log);
        });
      }
    });

  },
  /**
   * Helper utility to route log statments to the correct log level and logging instance
   * @param {string} level - The log severity to use
   * @param {string} msg - The message that should be logged
   * @param {Object} Logger - The logger object that should be used to log the message
   */
  logRouter: function (level, msg, Logger) {
    if (hasInitialized) {
      LoggerCollection[Logger.getNamespace()][level](msg);
    } else {
      Logger[level](msg);
    }
  },
  /**
   * Add HTTP headers needed for the remote logging service to categorize and identify the log file.
   * @param {Object} xhr - The browser XHR object that will be used to send the log data
   */
  addRequestHeadersToAjaxLogging: function (xhr) {
    xhr.setRequestHeader('X-Cardinal-ApiId', 'ApiId-' + cache.apiId);
    xhr.setRequestHeader('X-Cardinal-McsId', 'McsId-' + cache.consumerSessionId);
    xhr.setRequestHeader('X-Cardinal-JTI', 'Jti-' + cache.jti);
    xhr.setRequestHeader('X-Cardinal-Tid', 'Tid-' + cache.tid);
  }
},
  publicMethods = {
    /**
     * Allows for other modules to inject setup loggers if they have created some before the logger library was loaded
     * @param {object} newSetupLogger - the instance of the setup logger to add
     */
    addToLoggerCollection: function(newSetupLogger){
      try{
        LoggerCollection[newSetupLogger.getNamespace()] = newSetupLogger;
      } catch(error){}
    },
    /**
     * Enables Ajax logging for info messages and up. This will cause all 'log.info' and up logs to be sent to remote logging.
     */
    enableAjaxLogging: function () {
      var appenders = baseLogger.appenders;

      // INSIGHT - Ajax logging is always on so that a logging queue is kept. When we decide to enable more rebust remote logging programmatically we simply lower the threshold of remote logging to 'info'.
      // Errors will continue to be sent with the log queue, however info and warns will also start to be sent.
      _.each(appenders, function (appender) {
        if ('url' in appender) {
          appender.level = 3000;
        }
      });
      jsn.setOptions({
        appenders: appenders
      });
    },
    /**
     * Forces remaining logs in queue to be pushed to remote logging service if remote logging is enabled.
     */
    flushLogs: function () {
      var appenders = baseLogger.appenders,
        ajaxAppender;
      _.each(appenders, function (appender) {
        if ('url' in appender) {
          ajaxAppender = appender;
        }
      });

      // A level of 5000 or more means we're only sending remote logs when an error is thrown. We should only flush if we're logging more than just errors
      if (ajaxAppender !== undefined && ajaxAppender.level < 5000) {
        var originalBatchSize = ajaxAppender.batchSize;
        ajaxAppender.batchSize = 1;
        jsn.setOptions({
          appenders: appenders
        });
        baseLogger.info('Flushing logs');
        ajaxAppender.batchSize = originalBatchSize;
        jsn.setOptions({
          appenders: appenders
        });
      }

    },
    /**
     * Setup the real logging framework
     * @param {Object} systemConfig - The System Config object
     * @param {Object} merchantLogSettings - The merchant logging setting configured with 'Cardinal.configure'
     * @param {Object} defaultLogLevel - The log level that should be used if the merchant does not specify a level
     */
    initializeLogger: function (systemConfig, merchantLogSettings, defaultLogLevel) {
      var consoleAppender = jsn.createConsoleAppender().setOptions(systemConfig.logging.console),
        logAppenders = [consoleAppender],
        // Adds backwards compatibility support for the original 'debug' configuration key. logging.level should be used moving forward
        merchantLoglevel = (merchantLogSettings.debug ? merchantLogSettings.debug : merchantLogSettings.level),
        logLevel = merchantLoglevel || defaultLogLevel,
        ajaxLogSettings = {}, ajaxAppender;

      if (systemConfig.logging.ajax.enabled) {
        _.merge(ajaxLogSettings, systemConfig.logging.ajax, { "beforeSend": privateMethods.addRequestHeadersToAjaxLogging });
        ajaxAppender = jsn.createAjaxAppender().setOptions(ajaxLogSettings);
        logAppenders.push(ajaxAppender);
      }

      switch (logLevel) {
        case CONST_LOGGING.LEVELS.VERBOSE:
          consoleAppender.setOptions({ level: systemConfig.logging.levels.verbose });
          break;
        case CONST_LOGGING.LEVELS.DEBUG:
          consoleAppender.setOptions({ level: systemConfig.logging.levels.debug });
          break;
        default:
          consoleAppender.setOptions({ level: systemConfig.logging.levels.off });
          break;
      }

      // INSIGHT - Setup the base logger that all other loggers inherit from. We may not use this logger to actually log, but any logger with 'Base.' will inherit the 'Base' loggers configurations 
      baseLogger = jsn('Base').setOptions({
        appenders: logAppenders
      });

      privateMethods.outputQueuedLogs();
      hasInitialized = true;
    },
    /**
     * A race condition free way to get an instance of a logger. This function will return a mock logger if the logging framework has not been setup yet. When the logging framework is setup, the mock logger will
     * automatically be emptied into the proper logger instance and the mock object will be replaced with the real logger. If the logging framework is already setup, a real logging instance is returned.
     * @param {String} logNamespace - The namespace that should be used in the log statement. This should always start with 'Base' to allow for proper configuration inheritance.
     * @returns {Object} Log object
     */
    getLoggerInstance: function (logNamespace) {
      if (hasInitialized) {
        return jsn(logNamespace);
      }

      var Logger, LoggerFacade;
      Logger = new SetupLogger(logNamespace);
      LoggerCollection[logNamespace] = Logger;

      LoggerFacade = {
        trace: _.partial(privateMethods.logRouter, 'trace', _, Logger),
        debug: _.partial(privateMethods.logRouter, 'debug', _, Logger),
        info: _.partial(privateMethods.logRouter, 'info', _, Logger),
        warn: _.partial(privateMethods.logRouter, 'warn', _, Logger),
        error: _.partial(privateMethods.logRouter, 'error', _, Logger)
      };

      return LoggerFacade;
    },
    /**
     * Utilities to sanitize data to log
     */
    sanitize: {
      /**
       * Removes sensitive card holder data from the order object, allowing the order object to be logged.
       * @param {Object} orderObject - The object that should be sanitized for logging
       * @returns {Object} orderObject - A sanitized version of the object input
       */
      orderObject: function (orderObject) {
        var clonedObject, accountObject;

        try {
          // INSIGHT - eslint eqeqeq is disabled here intentionally to catch both null and undefined.
          if (orderObject != undefined && orderObject.Consumer != undefined) { // eslint-disable-line eqeqeq
            clonedObject = _.cloneDeep(orderObject);

            if (clonedObject.Consumer.Account !== undefined) {
              accountObject = clonedObject.Consumer.Account;

              if (accountObject.AccountNumber !== undefined) {
                clonedObject.Consumer.Account.AccountNumber = "****";
              }

              if (accountObject.CardCode !== undefined) {
                clonedObject.Consumer.Account.CardCode = "****";
              }

            }

            return clonedObject;
          }

        } catch (error) {
          baseLogger.error('Sanitize Order object resulted in an error: ' + error);
        }

        return orderObject;
      }
    }
  };

module.exports = publicMethods;


//////////////////
// WEBPACK FOOTER
// ./libraries/logger.js
// module id = 15
// module chunks = 0 5 20